import React, {useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useSearch} from '@tanstack/react-router';
import {useCountry} from '@resurs-internal/rb-app-api';
import {useTranslate} from 'hooks';
import {BankIDCallbackPage} from '../../../pages/bankid/BankIDCallbackPage';
import {getUpcomingDate} from '../utils/DepositeMonthlyDepositUtils';

export type MonthlyDepositCallbackAction = 'register' | 'cancel';

export const MONTHLY_DEPOSIT_CALLBACK_PATH = '/monthly-deposit/callback';

export const DepositAccountMonthlyDepositCallbackPage: React.FC = () => {
  const client = useQueryClient();
  const translate = useTranslate();
  const country = useCountry();
  const search = useSearch({from: '/_protected/monthly-deposit/callback'});
  const {status, accountId, action, amount, day} = search;
  const isSuccess = status === 'SUCCESSFUL';
  const path = `/home/accounts/deposits/${accountId}`;

  useEffect(() => {
    if (!isSuccess) return;
    client.removeQueries({queryKey: ['accounts', 'deposit']});
    client.removeQueries({queryKey: ['account', 'deposit']});
  }, [client, isSuccess]);

  if (!isSuccess) {
    return (
      <BankIDCallbackPage
        variant={'error'}
        title={translate('savings.deposit.monthly.error.title')}
        subtitle={translate('common.error.message')}
        path={path}
      />
    );
  }

  if (action === 'cancel') {
    return (
      <BankIDCallbackPage
        variant={'success'}
        title={translate('savings.deposit.monthly.cancel.success-title')}
        path={path}
      />
    );
  }

  const withdrawalDay = Number(day);
  const withdrawalAmount = Number(amount);
  const hasDepositDetails =
    Number.isFinite(withdrawalDay) &&
    withdrawalDay > 0 &&
    Number.isFinite(withdrawalAmount);

  return (
    <BankIDCallbackPage
      variant={'success'}
      title={translate('savings.deposit.monthly.success.title')}
      subtitle={
        hasDepositDetails
          ? translate('savings.deposit.monthly.success.body', [
              country.formatAmount({
                amount: withdrawalAmount,
                currency: country.currency,
              }),
              getUpcomingDate(withdrawalDay, country.locale),
              withdrawalDay.toString(),
            ])
          : translate('savings.deposit.monthly.success.caption')
      }
      path={path}
    />
  );
};
